<!-- Main Container (Dark Mode) -->
<div
  class="h-full flex border border-slate-800 rounded-xl flex-col bg-slate-950 text-slate-100 overflow-hidden"
>
  @if (!currentNote()) {
    <!-- List View -->
    <div
      class="flex-1 flex flex-col max-w-300 w-full mx-auto p-4 md:p-6 overflow-hidden"
    >
      <!-- Header Area -->
      <div class="flex items-center justify-between mb-4">
        <div class="relative">
          <h2
            class="text-2xl font-bold text-slate-100 tracking-tight flex items-center gap-2"
          >
            <span class="material-symbols-outlined text-indigo-400"
              >description</span
            >
            快速筆記
          </h2>
        </div>
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-1 shadow-sm cursor-pointer"
            (click)="createNote()"
          >
            <span class="material-symbols-outlined text-[18px]">add</span>
            新增筆記
          </button>
          <button
            type="button"
            class="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 cursor-pointer"
            (click)="showList()"
          >
            <span class="material-symbols-outlined text-[18px]">refresh</span>
            重新顯示
          </button>
          <button
            type="button"
            class="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-indigo-300 border border-slate-700 hover:border-indigo-500 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 shadow-sm cursor-pointer"
            (click)="toggleArchiveModal()"
          >
            <span class="material-symbols-outlined text-[18px]"
              >inventory_2</span
            >
            歸檔
          </button>
        </div>
      </div>

      <!-- Controls Area -->
      <div class="flex flex-row gap-4 mb-4 shrink-0">
        <div #search class="relative w-1/2 min-w-0">
          <span
            class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[20px] text-slate-400"
            >search</span
          >
          <input
            type="text"
            class="w-full bg-slate-900 border border-slate-800 rounded-lg pl-10 pr-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-colors"
            placeholder="搜尋筆記..."
            [ngModel]="query()"
            (ngModelChange)="query.set($event); scheduleSearch()"
          />
        </div>
        <div #order class="w-1/2 min-w-0">
          <app-jkcombobox
            class="jk-combobox-border block w-full"
            [styleDisplay]="'flex'"
            label="排序"
            [value]="mtrwlSrv.noteOrder()"
            (valueChange)="mtrwlSrv.noteOrder.set($event); loadNotes()"
            [items]="mtrwlSrv.noteOrderList()"
          >
          </app-jkcombobox>
        </div>
      </div>

      @if (errorMessage()) {
        <div
          class="bg-rose-950/80 border border-rose-800 text-rose-200 p-3 rounded-lg mb-4 text-sm"
        >
          {{ errorMessage() }}
        </div>
      }

      <!-- Notes Grid -->
      <div
        class="flex-1 overflow-y-auto min-h-0 pr-2 -mr-2"
        (scroll)="onListScroll($event)"
      >
        @if (loading()) {
          <div class="p-8 text-center text-slate-400 text-sm">載入中...</div>
        } @else if (notes().length === 0) {
          <div class="p-8 text-center text-slate-400 text-sm">找不到筆記。</div>
        } @else {
          <div
            class="w-full gap-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 pb-4 content-start"
          >
            @for (note of visibleNotes(); track note.WL001 + note.WL002) {
              <!-- Note Card (Dark Mode) -->
              <div
                class="bg-slate-900 border border-slate-800 hover:border-indigo-500/50 rounded-xl p-4 flex flex-col h-52 transition-all hover:shadow-xl hover:shadow-indigo-950/20 cursor-pointer group relative overflow-hidden"
                (click)="openNote(note)"
              >
                <div class="flex justify-between items-start mb-2 gap-2">
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 mb-1">
                      <span
                        class="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-mono bg-slate-800 text-slate-300 border border-slate-700"
                      >
                        {{ getCategoryLabel(note.WL014) }}
                      </span>
                    </div>
                    <h3
                      class="text-base font-semibold text-slate-100 line-clamp-1 group-hover:text-indigo-300 transition-colors"
                    >
                      {{ note.WL007 || "無標題" }}
                    </h3>
                  </div>
                  <div class="ai-star-center">
                    <app-jkstarmark
                      [value]="note.WL008"
                      [isUpdate]="false"
                      (onClick)="mtrwlSrv.toggleStar(note)"
                    >
                    </app-jkstarmark>
                  </div>
                  <button
                    class="text-slate-500 hover:text-amber-400 transition-colors -mt-1 -mr-1 p-1 rounded-full hover:bg-slate-800 opacity-0 group-hover:opacity-100 focus:opacity-100 shrink-0"
                    [class.text-amber-400]="note.WL008 === 'Y'"
                    [class.opacity-100]="note.WL008 === 'Y'"
                    (click)="toggleStar(note, $event)"
                  >
                    <span
                      class="material-symbols-outlined text-[20px]"
                      [attr.data-weight]="note.WL008 === 'Y' ? 'fill' : null"
                      >star</span
                    >
                  </button>
                </div>

                <div class="flex-1 overflow-hidden relative">
                  <p
                    class="text-xs text-slate-400 whitespace-pre-wrap wrap-break-words leading-relaxed line-clamp-3"
                  >
                    {{ note.WL013 }}
                  </p>
                  <div
                    class="absolute bottom-0 left-0 w-full h-8 bg-linear-to-t from-slate-900 to-transparent"
                  ></div>
                </div>

                <div
                  class="mt-3 pt-2.5 border-t border-slate-800/80 flex items-center justify-between text-[11px] font-mono text-slate-500 shrink-0"
                >
                  <span class="truncate">
                    {{ note.WL001 }} | 排序:{{ note.WL003 }} | 日期:{{
                      note.WL005
                    }}
                    @if (note.WL015) {
                      <span class="text-indigo-400 font-semibold ml-1"
                        >| 時數: {{ note.WL015 }}h</span
                      >
                    }
                  </span>
                  <span
                    class="material-symbols-outlined text-[16px] opacity-0 group-hover:opacity-100 transition-opacity text-indigo-400"
                    >open_in_new</span
                  >
                </div>
              </div>
            }
          </div>
        }
      </div>
    </div>
  } @else {
    <!-- Editor View (Dark Mode) -->
    <div
      class="flex-1 flex flex-col max-w-300 w-full mx-auto bg-slate-900 md:border-x md:border-slate-800 h-full overflow-hidden"
    >
      <!-- Editor Toolbar -->
      <div
        class="h-14 flex items-center justify-between px-4 border-b border-slate-800 bg-slate-950 shrink-0"
      >
        <div class="flex items-center gap-2">
          <button
            class="w-10 h-10 rounded-full hover:bg-slate-800 flex items-center justify-center text-slate-300 hover:text-slate-100 transition-colors cursor-pointer"
            (click)="showList()"
          >
            <span class="material-symbols-outlined text-[20px]"
              >arrow_back</span
            >
          </button>
          <div class="h-4 w-px bg-slate-800 mx-1"></div>

          <div class="flex items-center gap-1 text-xs font-mono">
            @switch (saveState()) {
              @case ("saving") {
                <span
                  class="material-symbols-outlined text-[16px] text-slate-400 animate-spin"
                  >sync</span
                >
                <span class="text-slate-400">儲存中...</span>
              }
              @case ("saved") {
                <span
                  class="material-symbols-outlined text-[16px] text-emerald-400"
                  >check_circle</span
                >
                <span class="text-emerald-400">已儲存</span>
              }
              @case ("editing") {
                <span class="text-amber-400">編輯中...</span>
              }
              @case ("failed") {
                <span
                  class="material-symbols-outlined text-[16px] text-rose-400"
                  >error</span
                >
                <span
                  class="text-rose-400 cursor-pointer underline"
                  (click)="retrySave()"
                  >儲存失敗 (重試)</span
                >
              }
            }
          </div>
        </div>

        <div class="flex items-center gap-2">
          @if (feedback()) {
            <span class="text-xs font-mono text-indigo-400 mr-2">{{
              feedback()
            }}</span>
          }
          <button
            class="px-3 py-1.5 rounded bg-slate-800 hover:bg-slate-700 text-indigo-300 text-xs font-medium border border-slate-700 transition-colors flex items-center gap-1 cursor-pointer"
            title="歸檔筆記"
            (click)="toggleArchiveModal()"
          >
            <span class="material-symbols-outlined text-[18px]"
              >inventory_2</span
            >
            歸檔
          </button>
          <button
            class="w-10 h-10 rounded hover:bg-slate-800 flex items-center justify-center text-slate-300 hover:text-slate-100 transition-colors group relative cursor-pointer"
            title="複製內容"
            (click)="copyContent()"
          >
            <span class="material-symbols-outlined text-[20px]"
              >content_copy</span
            >
          </button>
          <button
            class="w-10 h-10 rounded hover:bg-slate-800 flex items-center justify-center text-slate-300 hover:text-amber-400 transition-colors cursor-pointer"
            title="切換星號"
            (click)="toggleStar(currentNote()!)"
            [class.text-amber-400]="currentNote()?.WL008 === 'Y'"
          >
            <span
              class="material-symbols-outlined text-[20px]"
              [attr.data-weight]="currentNote()?.WL008 === 'Y' ? 'fill' : null"
              >star</span
            >
          </button>
          <button
            class="w-10 h-10 rounded hover:bg-rose-950/80 hover:text-rose-400 flex items-center justify-center text-slate-300 transition-colors cursor-pointer"
            title="刪除筆記"
            (click)="deleteCurrent()"
          >
            <span class="material-symbols-outlined text-[20px]">delete</span>
          </button>
        </div>
      </div>

      <!-- Editor Canvas -->
      <div class="flex-1 p-4 md:p-6 overflow-y-auto">
        @if (errorMessage() && saveState() === "failed") {
          <div
            class="bg-rose-950/80 border border-rose-800 text-rose-200 p-3 rounded-lg mb-4 text-sm"
          >
            {{ errorMessage() }}
          </div>
        }

        <!-- Field Control Bar -->
        <div
          class="flex flex-wrap items-center gap-3 mb-6 bg-slate-950/60 p-3 rounded-xl border border-slate-800"
        >
          <app-jkcombobox
            class="jk-combobox-border"
            [styleDisplay]="'flex'"
            label="大分類"
            [value]="category()"
            (valueChange)="categoryChange($event)"
            [items]="mtrwlSrv.mtrWd015List"
          >
          </app-jkcombobox>

          <div
            class="flex items-center gap-1 bg-slate-900 border border-slate-800 px-2.5 py-1.5 rounded-lg text-xs"
          >
            <span class="text-slate-400">序號:</span>
            <span class="font-mono text-slate-200 font-semibold">{{
              wl002()
            }}</span>
          </div>

          <div
            class="flex items-center gap-1.5 bg-slate-900 border border-slate-800 px-2.5 py-1 rounded-lg"
          >
            <label class="text-xs font-medium text-slate-400 shrink-0"
              >排序碼:</label
            >
            <input
              type="number"
              class="w-16 bg-slate-950 border border-slate-700 rounded px-2 py-0.5 text-slate-100 text-sm outline-none focus:border-indigo-500"
              placeholder="01-99"
              [ngModel]="wl003()"
              (ngModelChange)="editWL003($event)"
            />
          </div>

          <app-jkdatefield
            labelMode="floating"
            label="日期"
            [value]="wl005()"
            [showClearButton]="true"
            (valueChange)="editWL005($event)"
          >
          </app-jkdatefield>

          <!-- WL015 時數 .放日期旁邊 -->
          <div
            class="flex items-center gap-1.5 bg-indigo-950/40 border border-indigo-800/80 px-3 py-1 rounded-lg"
          >
            <label class="text-xs font-medium text-indigo-300 shrink-0"
              >時數(WL015):</label
            >
            <input
              type="number"
              step="0.5"
              min="0"
              class="w-20 bg-slate-950 border border-indigo-700/80 rounded px-2 py-0.5 text-slate-100 text-sm outline-none focus:border-indigo-400"
              placeholder="0"
              [ngModel]="wl015()"
              (ngModelChange)="editWL015($event)"
            />
            <span class="text-xs text-indigo-300">小時</span>
          </div>
        </div>

        <!-- Title Input -->
        <input
          type="text"
          class="w-full bg-transparent text-2xl font-bold text-slate-100 border-b border-slate-800 pb-3 outline-none placeholder:text-slate-600 mb-6 focus:border-indigo-500 transition-colors"
          placeholder="無標題筆記"
          [ngModel]="subject()"
          (ngModelChange)="editSubject($event)"
        />
        <!-- Content Textarea -->
        <!-- <div class="w-full min-h-125 border rounded">
          <textarea
            class="w-full h-full min-h-125 bg-transparent text-body-lg font-body-lg text-on-surface-variant border-none outline-none resize-none placeholder:text-outline-variant leading-relaxed"
            placeholder="開始輸入內容..."
            [ngModel]="content()"
            (ngModelChange)="editContent($event)"
          >
          </textarea>
        </div> -->
      <app-jkcheckbox
        class="jk-combobox-border block w-full"
        label="使用TiptapEditor"
        [value]="isTiptapEditor()"
        (valueChange)="isTiptapEditor.set($event)"
      >
      </app-jkcheckbox>
        @if(isTiptapEditor()) {
          <app-tiptap-markdown-editor [ngModel]="content()" (ngModelChange)="editContent($event)"  [required]="true">
          </app-tiptap-markdown-editor>
        }
        @if(!isTiptapEditor()){
        <!-- EasyMDE Markdown Editor with dark mode -->
        <app-easymde-markdown-editor
          [markdown]="content()"
          (markdownChange)="editContent($event)"
          [darkMode]="false"
        ></app-easymde-markdown-editor>
        }
      </div>
    </div>
  }
</div>

<!-- Archive Option Modal Popup -->
@if (showArchiveModal()) {
  <div
    class="fixed inset-0 bg-slate-950/80 backdrop-blur-xs z-50 flex items-center justify-center p-4"
    (click)="showArchiveModal.set(false)"
  >
    <div
      class="bg-slate-900 border border-slate-800 rounded-xl shadow-2xl max-w-md w-full p-6 text-slate-100"
      (click)="$event.stopPropagation()"
    >
      <div
        class="flex items-center justify-between border-b border-slate-800 pb-3 mb-4"
      >
        <h3 class="text-lg font-bold flex items-center gap-2 text-indigo-400">
          <span class="material-symbols-outlined">inventory_2</span>
          選擇歸檔方式
        </h3>
        <button
          (click)="showArchiveModal.set(false)"
          class="text-slate-400 hover:text-slate-200 cursor-pointer"
        >
          <span class="material-symbols-outlined">close</span>
        </button>
      </div>

      <p class="text-xs text-slate-400 mb-4">
        請選擇您希望採用的筆記歸檔方式：
      </p>

      <div class="space-y-3">
        <button
          type="button"
          class="w-full text-left p-4 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-indigo-500 transition-all group cursor-pointer"
          (click)="archiveSameDateToWorkCategory()"
        >
          <div
            class="font-semibold text-sm text-slate-100 group-hover:text-indigo-300 flex items-center gap-2"
          >
            <span class="material-symbols-outlined text-indigo-400 text-lg"
              >calendar_today</span
            >
            1. 同一日期合併歸到同一分類單頭 (工作類)
          </div>
          <div class="text-xs text-slate-400 mt-1 pl-7">
            將相同日期的筆記資料合併歸納至同一工作類別單頭中。
          </div>
        </button>

        <button
          type="button"
          class="w-full text-left p-4 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-indigo-500 transition-all group cursor-pointer"
          (click)="archiveSameMonthDiary()"
        >
          <div
            class="font-semibold text-sm text-slate-100 group-hover:text-indigo-300 flex items-center gap-2"
          >
            <span class="material-symbols-outlined text-indigo-400 text-lg"
              >edit_note</span
            >
            2. 同一月份單身 (日記)
          </div>
          <div class="text-xs text-slate-400 mt-1 pl-7">
            將相同月份的筆記整理歸檔為日記單身明細。
          </div>
        </button>
      </div>

      <div class="mt-6 flex justify-end">
        <button
          type="button"
          class="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm rounded-lg border border-slate-700 transition-colors cursor-pointer"
          (click)="showArchiveModal.set(false)"
        >
          取消
        </button>
      </div>
    </div>
  </div>
}
